import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { IoIosArrowBack } from "react-icons/io";
import { AiOutlineContacts } from "react-icons/ai";
import { SlLocationPin } from "react-icons/sl";
import { BsCircle, BsCheckCircleFill } from "react-icons/bs";
import httpClient from '../../utils/HttpClient';
import { RECEIVERS_URL } from '../../constants/Url';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';

export default function AddAddressPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = useLocation();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    region: '',
    detail: '',
    isDefault: false
  });
  const [pasteContent, setPasteContent] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleParse = async () => {
    let textToParse = pasteContent;
    if (!textToParse) {
      try {
        textToParse = await navigator.clipboard.readText();
      } catch (err) {
        console.error('Failed to read clipboard contents: ', err);
        return;
      }
    }
    
    if (!textToParse) return;

    let phone = '';
    const phoneMatch = textToParse.match(/1[3-9]\d{9}/);
    if (phoneMatch) {
      phone = phoneMatch[0];
      textToParse = textToParse.replace(phone, ' ');
    }

    // Split remaining by common delimiters
    const parts = textToParse.split(/[,，\s\n]+/).filter(Boolean);
    let name = '';
    let addressStr = '';

    if (parts.length > 0) {
      // Very basic heuristic for Chinese names
      const nameIndex = parts.findIndex(p => p.length >= 2 && p.length <= 4 && !/(省|市|区|县|镇|村|路|街|号|栋)/.test(p));
      if (nameIndex !== -1) {
        name = parts[nameIndex];
        parts.splice(nameIndex, 1);
      } else {
        name = parts[0];
        parts.shift();
      }
      addressStr = parts.join(' ');
    }

    // Try to extract Region vs Detail
    let region = '';
    let detail = '';
    const regionMatch = addressStr.match(/(.+?(省|自治区|行政区))?(.+?(市|自治州))?(.+?(区|县|市|旗))?(.*)/);
    
    if (regionMatch && (regionMatch[1] || regionMatch[3] || regionMatch[5])) {
      region = `${regionMatch[1] || ''}${regionMatch[3] || ''}${regionMatch[5] || ''}`.trim();
      detail = (regionMatch[7] || '').trim();
    } else {
      detail = addressStr.trim();
    }

    setFormData(prev => ({
      ...prev,
      name: name || prev.name,
      phone: phone || prev.phone,
      region: region || prev.region,
      detail: detail || prev.detail
    }));
    
    setPasteContent('');
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async () => {
    if (submitting) return;

    if (!formData.name || !formData.phone || !formData.detail) {
      message.error(t("text_fill_complete_address", "请填写完整的地址信息！"));
      return;
    }

    let province = '', city = '', district = '';
    const regionStr = formData.region || '';
    const regionMatch = regionStr.match(/(.+?(省|自治区|行政区))?(.+?(市|自治州))?(.+?(区|县|市|旗))?/);
    if (regionMatch && (regionMatch[1] || regionMatch[3] || regionMatch[5])) {
      province = regionMatch[1] || '';
      city = regionMatch[3] || '';
      district = regionMatch[5] || '';
    } else {
      const parts = regionStr.trim().split(/\s+/);
      if (parts.length >= 3) {
        province = parts[0];
        city = parts[1];
        district = parts.slice(2).join(' ');
      } else if (parts.length === 2) {
        province = parts[0];
        city = parts[1];
      } else {
        province = regionStr;
      }
    }

    // Fix for municipalities (like Beijing, Shanghai) where province is often omitted
    if (!province && city) {
      province = city;
    } else if (!province && !city) {
      province = regionStr || '';
    }

    // Ensure no empty fields to satisfy backend schema
    if (!city) city = province;
    if (!district) district = city;

    const payload = {
      recipientName: formData.name,
      phone: { countryCode: 86, number: formData.phone },
      province,
      city,
      district,
      addressLine1: formData.detail,
      addressLine2: '',
      country: "China",
      zipCode: "000000",
      isDefault: formData.isDefault
    };

    try {
      setSubmitting(true);
      await httpClient.post(RECEIVERS_URL, payload);
      navigate('/address-list', { state: location.state });
    } catch (err) {
      console.error("Failed to add address:", err);
      message.error(t("text_add_address_failed", "添加地址失败，请重试！"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="w-full max-w-3xl bg-[#f7f8fa] lg:bg-transparent flex flex-col relative mx-auto lg:my-10 lg:px-8">
      {/* Header */}
      <div className="bg-white/80 backdrop-blur-md flex items-center justify-center relative py-4 px-4 shadow-sm z-20 sticky top-0 transition-all duration-300 lg:hidden">
        <button 
          onClick={() => navigate(-1)} 
          className="absolute left-4 text-xl text-gray-800 hover:scale-110 hover:text-[#e80d44] transition-all duration-300"
        >
          <IoIosArrowBack />
        </button>
        <h1 className="text-lg font-medium text-gray-900 tracking-wider">{t("text_add_new_address")}</h1>
      </div>

      <div className="hidden lg:flex justify-between items-end mb-8">
        <h1 className="text-3xl font-bold text-gray-900 tracking-wider">{t("text_add_new_address")}</h1>
        <button 
          onClick={handleSave}
          disabled={submitting}
          className={`bg-gradient-to-r from-[#e80d44] to-[#ff3b68] text-white px-10 py-3 rounded-full font-bold transition-all ${submitting ? 'opacity-70 cursor-not-allowed' : 'hover:opacity-95'}`}
        >
          {submitting ? t("text_saving", "保存中...") : t("text_save_address")}
        </button>
      </div>

      <div className="px-4 lg:px-0 py-4 space-y-4 lg:space-y-6 flex-1 overflow-y-auto pb-24 lg:pb-10">
        {/* Smart Parse Box */}
        <div className="bg-white rounded-2xl p-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100 group">
          <textarea 
            value={pasteContent}
            onChange={(e) => setPasteContent(e.target.value)}
            placeholder={t("text_paste_address")}
            className="w-full bg-transparent border-none outline-none resize-none text-sm text-gray-700 placeholder-gray-400 min-h-[70px] leading-relaxed"
          />
          <div className="flex justify-end mt-3">
            <button 
              onClick={handleParse}
              className="bg-[#fff0f4] text-[#e80d44] px-5 py-2 rounded-full text-xs font-medium hover:bg-[#e80d44] hover:text-white transition-colors duration-300 shadow-sm"
            >
              {t("text_paste_and_recognise")}
            </button>
          </div>
        </div>

        {/* Form Box */}
        <div className="bg-white rounded-2xl px-5 shadow-[0_4px_20px_rgba(0,0,0,0.02)] border-2 border-gray-100">
          {/* Name */}
          <div className="flex items-center py-4 border-b border-gray-50 group focus-within:border-[#e80d44]/30 transition-colors">
            <label className="w-20 text-gray-800 font-semibold text-sm tracking-wide">{t("text_receiver_name")}</label>
            <input 
              type="text" 
              name="name"
              placeholder={t("text_receiver_name_placeholder") }
              value={formData.name}
              onChange={handleChange}
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400"
            />
            <AiOutlineContacts className="text-gray-300 text-xl ml-2 group-focus-within:text-[#e80d44] transition-colors" />
          </div>

          {/* Phone */}
          <div className="flex items-center py-4 border-b border-gray-50 group focus-within:border-[#e80d44]/30 transition-colors">
            <label className="w-20 text-gray-800 font-semibold text-sm tracking-wide">{t("text_receiver_phone")}</label>
            <input 
              type="tel" 
              name="phone"
              placeholder={t("text_receiver_phone_placeholder")} 
              value={formData.phone}
              onChange={handleChange}
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400"
            />
          </div>

          {/* Region */}
          <div className="flex items-center py-4 border-b border-gray-50 group focus-within:border-[#e80d44]/30 transition-colors">
            <label className="w-20 text-gray-800 font-semibold text-sm tracking-wide">{t("text_address")}</label>
            <input 
              type="text" 
              name="region"
              placeholder={t("text_address_placeholder")} 
              value={formData.region}
              onChange={handleChange}
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400"
            />
          </div>

          {/* Detail */}
          <div className="flex items-center py-4 border-b border-gray-50 group focus-within:border-[#e80d44]/30 transition-colors">
            <label className="w-20 text-gray-800 font-semibold text-sm tracking-wide">{t("text_detail_address")}</label>
            <input 
              type="text" 
              name="detail"
              placeholder={t("text_detail_address_placeholder")} 
              value={formData.detail}
              onChange={handleChange}
              className="flex-1 bg-transparent border-none outline-none text-sm text-gray-900 placeholder-gray-400"
            />
            <SlLocationPin className="text-gray-300 text-lg ml-2 group-focus-within:text-[#e80d44] transition-colors" />
          </div>

          {/* Set Default */}
          <div className="flex items-center justify-between py-5">
            <button 
              onClick={() => setFormData({ ...formData, isDefault: !formData.isDefault })}
              className="flex items-center gap-3 group hover:opacity-80 transition-opacity"
            >
              {formData.isDefault ? (
                <BsCheckCircleFill className="text-[#e80d44] text-xl shadow-sm rounded-full" />
              ) : (
                <BsCircle className="text-gray-300 text-xl group-hover:border-gray-400 transition-colors" />
              )}
              <span className={`text-sm font-medium tracking-wide transition-colors ${formData.isDefault ? 'text-[#e80d44]' : 'text-gray-700'}`}>{t("text_set_default_address")}</span>
            </button>
            <button 
              onClick={() => setFormData({ name: '', phone: '', region: '', detail: '', isDefault: false })}
              className="text-gray-400 text-sm hover:text-gray-800 transition-colors"
            >
              {t('text_clear')}
            </button>
          </div>
        </div>
      </div>

      {/* Bottom Sticky Button (Mobile Only) */}
      <div className="fixed lg:hidden bottom-0 left-0 right-0 w-full bg-white/90 backdrop-blur-md border-t-2 border-black/5 p-4 z-20 shadow-[0_-8px_30px_-5px_rgba(0,0,0,0.08)]">
        <button 
          onClick={handleSave}
          disabled={submitting}
          className={`w-full bg-gradient-to-r from-[#e80d44] to-[#ff3b68] text-white py-3.5 rounded-full font-semibold text-base shadow-lg shadow-[#e80d44]/30 hover:shadow-[#e80d44]/50 transition-all duration-300 ${submitting ? 'opacity-70 cursor-not-allowed' : 'hover:scale-[1.02] active:scale-[0.98]'}`}
        >
          {submitting ? t("text_saving", "保存中...") : t("text_save_address")}
        </button>
      </div>
    </div>
  );
}
